// Project ↔ project sequencing links.
//
// A link is declared on ONE project — either in `sequencing.depends_on_projects`
// ("X must come before me") or in `sequencing.enables_projects` ("I come before
// Y"). The other end never stores a copy: it is derived here, so both ends of an
// edge always agree and there is nothing to keep in sync.
//
// This module is the single source of truth for reading those links. Nothing
// else should parse `sequencing.*_projects` directly.

import type { Project } from '../types';

export interface ProjectLinkView {
  /** null when the item names a project that doesn't exist (AI suggestion, future project). */
  projectId: number | null;
  name: string;
  reason: string;
  /** true when this project declared the link; false when it comes from the other end. */
  declared: boolean;
}

export interface ProjectLinkSet {
  /** Projects that must come before this one. */
  blockers: ProjectLinkView[];
  /** Projects that this one unblocks. */
  blocked: ProjectLinkView[];
}

export type ProjectLinkIndex = Map<number, ProjectLinkSet>;

type SeqItem = { name?: string; reason?: string; project_id?: number | null };

function parseJSON<T>(val: any, fallback: T): T {
  if (!val) return fallback;
  if (typeof val === 'object') return val as T;
  try { return JSON.parse(val); } catch { return fallback; }
}

function seqItems(project: Project, key: 'depends_on_projects' | 'enables_projects'): SeqItem[] {
  const seq = parseJSON<any>(project.sequencing, null);
  const list = seq?.[key];
  return Array.isArray(list) ? list : [];
}

const norm = (s: string | undefined | null) => (s || '').trim().toLowerCase();

/**
 * Name → id, for items the AI (or an older import) left without a project_id.
 * Ambiguous names resolve to nothing rather than to the wrong project.
 */
function buildNameIndex(projects: Project[]): Map<string, number | null> {
  const byName = new Map<string, number | null>();
  for (const p of projects) {
    const key = norm(p.name);
    if (!key) continue;
    byName.set(key, byName.has(key) ? null : p.id);
  }
  return byName;
}

function resolveId(item: SeqItem, byName: Map<string, number | null>, known: Set<number>): number | null {
  if (item.project_id != null && known.has(item.project_id)) return item.project_id;
  return byName.get(norm(item.name)) ?? null;
}

const dedupeKey = (v: ProjectLinkView) => (v.projectId != null ? `id:${v.projectId}` : `name:${norm(v.name)}`);

/** Declared entries win over derived ones; the first non-empty reason is kept. */
function push(list: ProjectLinkView[], entry: ProjectLinkView) {
  const key = dedupeKey(entry);
  const existing = list.find(v => dedupeKey(v) === key);
  if (!existing) { list.push(entry); return; }
  if (entry.declared) existing.declared = true;
  if (!existing.reason && entry.reason) existing.reason = entry.reason;
}

const EMPTY: ProjectLinkSet = { blockers: [], blocked: [] };

/**
 * Resolve every project's links in one pass — O(projects + links), so the card
 * grids can look up a project without re-scanning the portfolio per card.
 */
export function buildProjectLinkIndex(projects: Project[]): ProjectLinkIndex {
  const byName = buildNameIndex(projects);
  const known = new Set(projects.map(p => p.id));
  const index: ProjectLinkIndex = new Map();
  const setFor = (id: number) => {
    let s = index.get(id);
    if (!s) { s = { blockers: [], blocked: [] }; index.set(id, s); }
    return s;
  };

  for (const p of projects) setFor(p.id);

  for (const p of projects) {
    const mine = setFor(p.id);

    for (const item of seqItems(p, 'depends_on_projects')) {
      const id = resolveId(item, byName, known);
      if (id === p.id) continue; // a project can't block itself
      const name = (id != null ? projects.find(x => x.id === id)?.name : null) || item.name || '';
      const reason = item.reason || '';
      push(mine.blockers, { projectId: id, name, reason, declared: true });
      if (id != null) push(setFor(id).blocked, { projectId: p.id, name: p.name, reason, declared: false });
    }

    for (const item of seqItems(p, 'enables_projects')) {
      const id = resolveId(item, byName, known);
      if (id === p.id) continue;
      const name = (id != null ? projects.find(x => x.id === id)?.name : null) || item.name || '';
      const reason = item.reason || '';
      push(mine.blocked, { projectId: id, name, reason, declared: true });
      if (id != null) push(setFor(id).blockers, { projectId: p.id, name: p.name, reason, declared: false });
    }
  }

  return index;
}

export function linksFor(index: ProjectLinkIndex | null, projectId: number): ProjectLinkSet {
  return index?.get(projectId) ?? EMPTY;
}

/** Counters for the project cards ("Nécessite N" / "Bloque N"). */
export function linkCounts(index: ProjectLinkIndex | null, projectId: number): { needs: number; blocks: number } {
  const set = linksFor(index, projectId);
  return { needs: set.blockers.length, blocks: set.blocked.length };
}

/**
 * Only the links OTHER projects declare toward this one. The project's own
 * declarations are ignored, so an editor can show what it doesn't own — and what
 * it therefore must not offer to add a second time.
 */
export function incomingLinksFor(projectId: number, projects: Project[]): ProjectLinkSet {
  const byName = buildNameIndex(projects);
  const known = new Set(projects.map(p => p.id));
  const self = projects.find(p => p.id === projectId);
  const out: ProjectLinkSet = { blockers: [], blocked: [] };

  const pointsAtSelf = (item: SeqItem) => {
    const id = resolveId(item, byName, known);
    return id != null ? id === projectId : !!self && norm(item.name) === norm(self.name);
  };

  for (const p of projects) {
    if (p.id === projectId) continue;
    // "p enables me" → p comes before me
    for (const item of seqItems(p, 'enables_projects')) {
      if (pointsAtSelf(item)) push(out.blockers, { projectId: p.id, name: p.name, reason: item.reason || '', declared: false });
    }
    // "p depends on me" → p comes after me
    for (const item of seqItems(p, 'depends_on_projects')) {
      if (pointsAtSelf(item)) push(out.blocked, { projectId: p.id, name: p.name, reason: item.reason || '', declared: false });
    }
  }
  return out;
}

/**
 * Links of a single project when no index is at hand (detail pages). Same result
 * as going through the index — it just builds one for the projects given.
 */
export function projectLinksFor(project: Project, allProjects: Project[]): ProjectLinkSet {
  const pool = allProjects.some(p => p.id === project.id) ? allProjects : [...allProjects, project];
  return linksFor(buildProjectLinkIndex(pool), project.id);
}
